import type { Form as ApiForm } from '@kizen/api/form';
import { createContext, useContext, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ConfirmDeletionModal from 'components/Modals/presets/ConfirmDeletion';
import useModal from '__components/Modals/useModal';
import { useToast, toastVariant } from '__components/ToastProvider';
import SurveyService from '__services/SurveyService';
import { deleteSurvey } from '__store/surveysPage/surveys.redux';

type Survey = SnakeToCamelCase<ApiForm>;

type SurveySettingsPageProviderProps = {
  children: ReactNode;
  survey: Survey;
};

type ErrorMessages = {
  name: string;
  redirectUrl: string;
};

type SurveySettingsPageValue = ReturnType<typeof useSurveySettings>;

const INITIAL_ERROR_MESSAGES: ErrorMessages = { name: '', redirectUrl: '' };

export const SUBMISSION_ACTIONS = {
  PAGE: 'go_to_page',
  URL: 'go_to_url',
} as const;

const SurveySettingsPageContext = createContext<SurveySettingsPageValue>({
  confirmDeleteModalProps: {},
  errorMessages: { name: '', redirectUrl: '' },
  survey: {} as Survey,
  submissionAction: undefined,
  setErrorMessages: () => {},
  setSubmissionAction: () => {},
  onExportResponses: () => Promise.resolve(),
  onShowConfirmDeleteModal: () => {},
});

const useSurveySettings = (survey: Survey) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [showToast] = useToast();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>(
    INITIAL_ERROR_MESSAGES
  );
  const [submissionAction, setSubmissionAction] = useState<
    Survey['submissionAction']
  >(survey.submissionAction);

  const onExportResponses = async () => {
    try {
      await SurveyService.exportResponses(survey);
      const message = t(
        `Responses were queued for export successfully. You will be emailed a download link once complete`
      );
      showToast({
        variant: toastVariant.SUCCESS,
        message,
      });
    } catch {
      const message = t(
        'The export was not successful. Please try again or contact Kizen support.'
      );
      showToast({
        variant: toastVariant.FAILURE,
        message,
      });
    }
  };

  const [confirmDeleteModalProps, , confirmDeleteModal] = useModal({
    handleSubmit: () => {
      dispatch(deleteSurvey(survey?.id));
      setSubmissionAction(SUBMISSION_ACTIONS.PAGE);
      setErrorMessages(INITIAL_ERROR_MESSAGES);
      // we do not wait for the delete result from BE here so we need to redirect when the state of component is updated
      setTimeout(() => history.replace('/surveys'));
    },
  });

  return {
    confirmDeleteModalProps,
    errorMessages,
    survey,
    submissionAction,
    setErrorMessages,
    setSubmissionAction,
    onExportResponses,
    onShowConfirmDeleteModal: confirmDeleteModal.show,
  };
};

export const useSurveySettingsPage = (): SurveySettingsPageValue => {
  return useContext(SurveySettingsPageContext);
};

export const SurveySettingsPageProvider = (
  props: SurveySettingsPageProviderProps
) => {
  const { children, survey } = props;
  const { t } = useTranslation();
  const ctx = useSurveySettings(survey);
  const { confirmDeleteModalProps } = ctx;

  return (
    <SurveySettingsPageContext.Provider value={ctx}>
      {children}
      <ConfirmDeletionModal {...confirmDeleteModalProps}>
        {t('This will permanently delete the survey.')}
      </ConfirmDeletionModal>
    </SurveySettingsPageContext.Provider>
  );
};
